import React, { useState, useEffect } from 'react'
import { Employee } from '@/types/workforce'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { listEmployees } from '@/api/workforce'
import { getTenantDomain } from '@/lib/services/api-utils'
import { X, Search, UserCircle, ArrowRight } from 'lucide-react'

interface ReassignManagerModalProps {
  employee: Employee
  currentManager?: Employee
  onConfirm: (newManagerId: string | null) => Promise<void>
  onCancel: () => void
}

export function ReassignManagerModal({
  employee,
  currentManager,
  onConfirm,
  onCancel
}: ReassignManagerModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [potentialManagers, setPotentialManagers] = useState<Employee[]>([])
  const [selectedManager, setSelectedManager] = useState<Employee | null>(currentManager || null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    loadPotentialManagers()
  }, [])

  const loadPotentialManagers = async () => {
    try {
      setLoading(true)
      const tenantDomain = getTenantDomain()
      const result = await listEmployees(tenantDomain, {
        status: 'ACTIVE',
        limit: 100
      })

      // Filtrar apenas o próprio funcionário (manter o gestor atual na lista)
      const filtered = result.items.filter(
        (emp) => emp.employeeId !== employee.employeeId
      )

      // Reordenar para colocar o gestor atual no topo
      const sorted = currentManager
        ? [
            // Gestor atual primeiro
            ...filtered.filter(emp => emp.employeeId === currentManager.employeeId),
            // Depois os outros
            ...filtered.filter(emp => emp.employeeId !== currentManager.employeeId)
          ]
        : filtered

      setPotentialManagers(sorted)
    } catch (error) {
      console.error('Error loading potential managers:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async () => {
    try {
      setSubmitting(true)
      // If selectedManager is null, pass null to remove manager
      await onConfirm(selectedManager ? selectedManager.employeeId : null)
    } catch (error) {
      console.error('Error reassigning manager:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const filteredManagers = potentialManagers.filter((manager) => {
    const name = manager.personalInfo?.name || ''
    const email = manager.email || ''
    const dept = manager.employment?.department?.name || ''
    const role = manager.employment?.role?.name || ''

    const search = searchTerm.toLowerCase()
    return (
      name.toLowerCase().includes(search) ||
      email.toLowerCase().includes(search) ||
      dept.toLowerCase().includes(search) ||
      role.toLowerCase().includes(search)
    )
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in">
      <Card className="max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-700/50">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-display text-2xl font-bold text-white">
                Reatribuir Gestor
              </h2>
              <p className="text-brand-gray text-sm mt-1 font-body">
                Selecione um novo gestor para {employee.personalInfo?.name}
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-brand-gray hover:text-white transition-colors duration-200"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Current Manager */}
        {currentManager && (
          <div className="p-6 bg-brand-dark-lighter/50 border-b border-gray-700/50">
            <p className="text-sm text-brand-gray mb-2 font-body">Gestor Atual:</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center">
                <UserCircle className="w-6 h-6 text-brand-dark" />
              </div>
              <div>
                <p className="font-display font-semibold text-white">
                  {currentManager.personalInfo?.name}
                </p>
                <p className="text-sm text-brand-gray">{currentManager.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-6 border-b border-gray-700/50">
          <div className="relative group">
            <Search className="w-4 h-4 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-brand-primary transition-colors duration-200 pointer-events-none z-10" />
            <Input
              type="text"
              placeholder="Buscar por nome, email, departamento..."
              className="pl-10"
              style={{ paddingLeft: '3rem' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Manager List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="space-y-2">
              {/* None Option - Remove Manager */}
              <button
                onClick={() => setSelectedManager(null)}
                className={`
                  w-full text-left p-4 rounded-monumenta transition-all duration-200
                  border
                  ${
                    selectedManager === null
                      ? 'border-brand-primary bg-brand-primary/10'
                      : 'border-transparent hover:border-brand-primary/40 hover:bg-brand-dark-lighter/50'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <X size={20} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-white">
                      Sem Gestor
                    </p>
                    <p className="text-sm text-brand-gray font-body">
                      Remover gestor atual (reporta diretamente à organização)
                    </p>
                  </div>
                  {selectedManager === null && (
                    <div className="text-brand-primary">
                      <ArrowRight size={20} />
                    </div>
                  )}
                </div>
              </button>

              {/* Managers List */}
              {filteredManagers.length === 0 && searchTerm ? (
                <div className="text-center py-12">
                  <UserCircle className="w-16 h-16 mx-auto mb-4 text-brand-gray" />
                  <p className="text-brand-gray font-body">
                    Nenhum gestor encontrado
                  </p>
                </div>
              ) : (
                filteredManagers.map((manager) => {
                  const name = manager.personalInfo?.name || manager.email
                  const nameParts = name.split(' ')
                  const initials =
                    nameParts.length >= 2
                      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
                      : `${nameParts[0][0]}${nameParts[0][1] || ''}`

                  const isSelected = selectedManager?.employeeId === manager.employeeId
                  const isCurrent = currentManager?.employeeId === manager.employeeId

                  return (
                    <button
                      key={manager.employeeId}
                      onClick={() => setSelectedManager(manager)}
                      className={`
                        w-full text-left p-4 rounded-monumenta transition-all duration-200
                        border
                        ${
                          isSelected
                            ? 'border-brand-primary bg-brand-primary/10'
                            : 'border-transparent hover:border-brand-primary/40 hover:bg-brand-dark-lighter/50'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-brand-dark font-display">
                            {initials.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-display font-semibold text-white truncate">
                              {name}
                            </p>
                            {isCurrent && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/20 text-brand-primary font-medium whitespace-nowrap">
                                Gestor Atual
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-brand-gray truncate font-body">
                            {manager.employment?.role?.name || 'Função não definida'} •{' '}
                            {manager.employment?.department?.name || 'Sem departamento'}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="text-brand-primary">
                            <ArrowRight size={20} />
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700/50 flex justify-end gap-3">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Processando...
              </>
            ) : (
              'Confirmar Reatribuição'
            )}
          </Button>
        </div>
      </Card>
    </div>
  )
}
